import { useState } from "react";
import api, { formatErr } from "@/lib/api";
import useSettings from "@/lib/useSettings";
import SectionHeader from "@/components/SectionHeader";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Contact() {
  const settings = useSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setStatus("");
    try {
      const { data } = await api.post("/contact", form);
      setStatus(data.message || "Wiadomość wysłana.");
      setForm({ name: "", email: "", message: "" });
    } catch (e2) {
      setError(formatErr(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-16" data-testid="contact-page">
      <SectionHeader title="Kontakt" />
      <div className="grid md:grid-cols-2 gap-12 mt-10">
        <div className="fade-up space-y-5">
          <p className="text-lg" style={{ color: "var(--sr-cream-soft)" }}>
            Masz pytania? Chcesz dołączyć do klubu lub zaprosić nas na wydarzenie? Napisz — odpowiemy.
          </p>
          <ul className="space-y-3 text-base" style={{ color: "var(--sr-cream-soft)" }}>
            <li className="flex items-center gap-3" data-testid="contact-email-line">
              <Mail className="text-[var(--sr-fire)]" size={18} />
              <a href={`mailto:${settings.contact_email || ""}`} className="hover:text-[var(--sr-fire)]">
                {settings.contact_email || "—"}
              </a>
            </li>
            <li className="flex items-center gap-3" data-testid="contact-phone-line">
              <Phone className="text-[var(--sr-fire)]" size={18} />
              <a href={`tel:${(settings.contact_phone || "").replace(/\s+/g, "")}`} className="hover:text-[var(--sr-fire)]">
                {settings.contact_phone || "—"}
              </a>
            </li>
            <li className="flex items-center gap-3" data-testid="contact-address-line">
              <MapPin className="text-[var(--sr-fire)]" size={18} /> {settings.contact_address || "—"}
            </li>
          </ul>
          <div className="mt-6 sr-card overflow-hidden" data-testid="contact-map">
            <iframe
              title="Lokalizacja klubu Scythe Riders"
              src={`https://www.google.com/maps?q=${encodeURIComponent(settings.map_query || "Kocmyrzów-Luborzyca,Polska")}&output=embed`}
              width="100%"
              height="280"
              style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <form onSubmit={submit} className="sr-card p-6 fade-up delay-2" data-testid="contact-form">
          <div className="mb-4">
            <label className="sr-label">Imię</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="sr-input"
              data-testid="contact-name"
            />
          </div>
          <div className="mb-4">
            <label className="sr-label">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="sr-input"
              data-testid="contact-email"
            />
          </div>
          <div className="mb-4">
            <label className="sr-label">Wiadomość</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="sr-input"
              data-testid="contact-message"
            />
          </div>
          {status && <p className="text-sm mb-3" style={{ color: "var(--sr-fire-2)" }} data-testid="contact-success">{status}</p>}
          {error && <p className="text-sm mb-3 text-red-400" data-testid="contact-error">{error}</p>}
          <button type="submit" className="btn-fire w-full" disabled={loading} data-testid="contact-submit">
            {loading ? "Wysyłanie..." : "Wyślij wiadomość"}
          </button>
        </form>
      </div>
    </div>
  );
}
